import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import {
  CreditCard, Shield, Zap, Save, TestTube2,
  Eye, EyeOff, FileText, Lock, Unlock, CheckCircle2, XCircle,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  PageHeader, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, AppBadge,
} from "../ui";
import "./RazorpaySettings.scss";

/* ── Toggle Switch ── */
function RzpToggle({ checked, onChange, id }) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      className={`rzp-toggle ${checked ? "rzp-toggle--on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span className="rzp-toggle__thumb" />
    </button>
  );
}

/* ── Masked Input ── */
function SecretInput({ value, onChange, placeholder, disabled }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="rzp-secret-wrap">
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="rzp-input"
      />
      <button
        type="button"
        className="rzp-secret-eye"
        onClick={() => setVisible((v) => !v)}
        disabled={disabled}
        tabIndex={-1}
      >
        {visible ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

/* ── Mode Pill Button ── */
function ModePill({ value, selected, label, emoji, onClick }) {
  return (
    <button
      type="button"
      className={`rzp-mode-btn ${selected ? "rzp-mode-btn--active" : ""} rzp-mode-btn--${value}`}
      onClick={onClick}
    >
      <span className="rzp-mode-btn__emoji">{emoji}</span>
      {label}
    </button>
  );
}

const RazorpaySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [editingKeys, setEditingKeys] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const [form, setForm] = useState({
    enabled: false,
    mode: "test",
    testKeyId: "",
    testKeySecret: "",
    liveKeyId: "",
    liveKeySecret: "",
    webhookSecret: "",
    invoicePrefix: "INV",
    receiptPrefix: "RCP",
    gstNumber: "",
  });

  useEffect(() => {
    setLoading(true);
    apiClient
      .get("/institute/razorpay-settings")
      .then(({ data }) => {
        setForm({
          enabled: data.enabled || false,
          mode: data.mode || "test",
          testKeyId: data.testKeyId || "",
          testKeySecret: "",
          liveKeyId: data.liveKeyId || "",
          liveKeySecret: "",
          webhookSecret: "",
          invoicePrefix: data.invoicePrefix || "INV",
          receiptPrefix: data.receiptPrefix || "RCP",
          gstNumber: data.gstNumber || "",
        });
      })
      .catch(() => {
        setToast({ show: true, message: "Failed to load Razorpay settings.", type: "error" });
      })
      .finally(() => setLoading(false));
  }, []);

  const update = (field) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const keyFieldsDisabled = !editingKeys;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.testKeySecret) delete payload.testKeySecret;
      if (!payload.liveKeySecret) delete payload.liveKeySecret;
      if (!payload.webhookSecret) delete payload.webhookSecret;

      await apiClient.put("/institute/razorpay-settings", payload);
      setToast({ show: true, message: "Razorpay settings saved!", type: "success" });
      setForm((f) => ({ ...f, testKeySecret: "", liveKeySecret: "", webhookSecret: "" }));
      setEditingKeys(false);
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Failed to save settings.";
      setToast({ show: true, message: msg, type: "error" });
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await apiClient.post("/institute/razorpay-settings/test");
      setTestResult({ success: true, message: `${data.message} (Mode: ${data.mode})` });
    } catch (ex) {
      const msg = ex?.response?.data?.error || ex?.response?.data?.message || "Connection test failed.";
      setTestResult({ success: false, message: msg });
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <div className="rzp-settings">
        <div className="settings-loading"><Spinner size="sm" /> Loading Razorpay settings...</div>
      </div>
    );
  }

  return (
    <div className="rzp-settings">
      <PageHeader
        icon={CreditCard}
        title="Payment Gateway"
        description="Configure Razorpay integration for online fee collection"
      />

      {/* ── Gateway Status — compact top bar ── */}
      <AppCard className="rzp-status-card">
        <AppCardContent>
          <div className="rzp-status-bar">
            <div className="rzp-status-bar__left">
              <div className="rzp-status-item">
                <Zap size={16} className="rzp-status-item__icon" />
                <span className="rzp-status-item__label">Online Payments</span>
                <RzpToggle
                  id="rzp-enabled"
                  checked={form.enabled}
                  onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                />
                <AppBadge
                  variant="default"
                  color={form.enabled ? "emerald" : "rose"}
                  size="sm"
                >
                  {form.enabled ? "Active" : "Inactive"}
                </AppBadge>
              </div>
            </div>
            <div className="rzp-status-bar__right">
              <span className="rzp-status-item__label">Environment</span>
              <div className="rzp-mode-toggle">
                <ModePill
                  value="test"
                  selected={form.mode === "test"}
                  label="Test"
                  emoji="🧪"
                  onClick={() => setForm((f) => ({ ...f, mode: "test" }))}
                />
                <ModePill
                  value="live"
                  selected={form.mode === "live"}
                  label="Live"
                  emoji="🟢"
                  onClick={() => setForm((f) => ({ ...f, mode: "live" }))}
                />
              </div>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── API Credentials — single card with lock ── */}
      <AppCard>
        <AppCardHeader>
          <div className="rzp-cred-header">
            <div>
              <AppCardTitle>
                <span className="rzp-card-title"><Shield size={18} /> API Credentials</span>
              </AppCardTitle>
              <p className="rzp-card-desc">Razorpay key pairs for test and live environments</p>
            </div>
            <AppButton
              variant={editingKeys ? "destructive" : "outline"}
              size="sm"
              onClick={() => setEditingKeys((v) => !v)}
            >
              {editingKeys ? <><Unlock size={14} className="me-1" /> Cancel</> : <><Lock size={14} className="me-1" /> Edit Keys</>}
            </AppButton>
          </div>
        </AppCardHeader>
        <AppCardContent>
          {/* Lock status banner */}
          <div className={`rzp-lock-banner ${editingKeys ? "rzp-lock-banner--unlocked" : ""}`}>
            {editingKeys
              ? <><Unlock size={14} /> Keys are unlocked — make your changes and save</>
              : <><Lock size={14} /> Keys are locked for security. Click "Edit Keys" to modify.</>}
          </div>

          {/* Test credentials */}
          <div className="rzp-cred-section">
            <h4 className="rzp-cred-section__title">
              Test Credentials
              <AppBadge variant="outline" color="amber" size="sm">Sandbox</AppBadge>
            </h4>
            <div className="rzp-field-grid">
              <div className="rzp-field">
                <label>Test Key ID</label>
                <input
                  type="text"
                  value={form.testKeyId}
                  onChange={update("testKeyId")}
                  placeholder="rzp_test_xxxxxxxxxx"
                  disabled={keyFieldsDisabled}
                  className="rzp-input"
                />
              </div>
              <div className="rzp-field">
                <label>Test Key Secret</label>
                <SecretInput
                  value={form.testKeySecret}
                  onChange={update("testKeySecret")}
                  placeholder="Leave blank to keep existing"
                  disabled={keyFieldsDisabled}
                />
              </div>
            </div>
          </div>

          <div className="rzp-divider" />

          {/* Live credentials */}
          <div className="rzp-cred-section">
            <h4 className="rzp-cred-section__title">
              Live Credentials
              <AppBadge variant="default" color="emerald" size="sm">Production</AppBadge>
            </h4>
            <div className="rzp-field-grid">
              <div className="rzp-field">
                <label>Live Key ID</label>
                <input
                  type="text"
                  value={form.liveKeyId}
                  onChange={update("liveKeyId")}
                  placeholder="rzp_live_xxxxxxxxxx"
                  disabled={keyFieldsDisabled}
                  className="rzp-input"
                />
              </div>
              <div className="rzp-field">
                <label>Live Key Secret</label>
                <SecretInput
                  value={form.liveKeySecret}
                  onChange={update("liveKeySecret")}
                  placeholder="Leave blank to keep existing"
                  disabled={keyFieldsDisabled}
                />
              </div>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Webhook & Invoicing ── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="rzp-card-title"><FileText size={18} /> Webhook & Invoicing</span>
          </AppCardTitle>
          <p className="rzp-card-desc">Webhook verification and invoice/receipt prefixes</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="rzp-field-grid rzp-field-grid--3col">
            <div className="rzp-field rzp-field--span2">
              <label>Webhook Secret</label>
              <SecretInput
                value={form.webhookSecret}
                onChange={update("webhookSecret")}
                placeholder="Leave blank to keep existing"
                disabled={keyFieldsDisabled}
              />
            </div>
            <div className="rzp-field">
              <label>GST Number</label>
              <input
                type="text"
                value={form.gstNumber}
                onChange={update("gstNumber")}
                placeholder="22AAAAA0000A1Z5"
                className="rzp-input"
              />
            </div>
          </div>
          <div className="rzp-field-grid">
            <div className="rzp-field">
              <label>Invoice Prefix</label>
              <input
                type="text"
                value={form.invoicePrefix}
                onChange={update("invoicePrefix")}
                placeholder="INV"
                maxLength={6}
                className="rzp-input"
              />
            </div>
            <div className="rzp-field">
              <label>Receipt Prefix</label>
              <input
                type="text"
                value={form.receiptPrefix}
                onChange={update("receiptPrefix")}
                placeholder="RCP"
                maxLength={6}
                className="rzp-input"
              />
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ── Action Bar ── */}
      <div className="rzp-action-bar">
        <div className="rzp-action-bar__left">
          {testResult && (
            <div className={`rzp-test-toast rzp-test-toast--${testResult.success ? "success" : "error"}`}>
              {testResult.success ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
              {testResult.message}
            </div>
          )}
        </div>
        <div className="rzp-action-bar__right">
          <AppButton
            variant="outline"
            onClick={handleTest}
            disabled={testing || !form.enabled}
          >
            {testing ? <><Spinner size="sm" className="me-1" /> Testing...</>
              : <><TestTube2 size={16} className="me-1" /> Test Connection</>}
          </AppButton>
          <AppButton onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner size="sm" className="me-1" /> Saving...</>
              : <><Save size={16} className="me-1" /> Save Settings</>}
          </AppButton>
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default RazorpaySettings;
